import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchAllAgentConfigs,
  fetchAgentConfig,
  createAgentConfig,
  updateAgentConfig,
} from "../actions/agentConfigActions";

// AgentConfig interface
export interface AgentConfig {
  id?: string;
  organization_id?: string;
  bot_name: string;
  bot_description: string;
  display_name?: string | null;
  override_personality_guidance?: string;
  created_at?: string;
  updated_at?: string | null;
  avatar_config?: {
    avatar_look?: string;
    avatar_voice?: string;
    avatar_voice_personality?: string;
    avatar_default_emotion?: string;
    avatar_language?: string;
    avatar_location?: string;
    avatar_pose?: string;
    environment?: string;
    shot?: string;
  };
  namespace_asset_registry?: {
    namespace_assets: Record<string, string[]>;
  } | null;
}

// State interface
interface AgentConfigState {
  agentConfigs: AgentConfig[];
  currentAgentConfig: AgentConfig | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AgentConfigState = {
  agentConfigs: [],
  currentAgentConfig: null,
  loading: false,
  error: null,
};

// Create the slice
const agentConfigSlice = createSlice({
  name: "agentConfigs",
  initialState,
  reducers: {
    clearCurrentAgentConfig(state) {
      state.currentAgentConfig = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<AgentConfigState>) => {
    // Handle fetchAllAgentConfigs
    builder
      .addCase(fetchAllAgentConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllAgentConfigs.fulfilled,
        (state, action: PayloadAction<AgentConfig[]>) => {
          state.loading = false;
          state.agentConfigs = action.payload;
        }
      )
      .addCase(fetchAllAgentConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch agent configs";
      });

    // Handle fetchAgentConfig
    builder
      .addCase(fetchAgentConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAgentConfig.fulfilled,
        (state, action: PayloadAction<AgentConfig>) => {
          state.loading = false;
          state.currentAgentConfig = action.payload;
        }
      )
      .addCase(fetchAgentConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch agent config";
      });

    // Handle createAgentConfig
    builder
      .addCase(createAgentConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createAgentConfig.fulfilled,
        (state, action: PayloadAction<AgentConfig>) => {
          state.loading = false;
          state.currentAgentConfig = action.payload;
          // Add the new config to the list
          state.agentConfigs.unshift(action.payload);
        }
      )
      .addCase(createAgentConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create agent config";
      });

    // Handle updateAgentConfig
    builder
      .addCase(updateAgentConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateAgentConfig.fulfilled,
        (state, action: PayloadAction<AgentConfig>) => {
          state.loading = false;
          state.currentAgentConfig = action.payload;
          // Update the config in the list
          const index = state.agentConfigs.findIndex(
            (config) => config.id === action.payload.id
          );
          if (index !== -1) {
            state.agentConfigs[index] = action.payload;
          }
        }
      )
      .addCase(updateAgentConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update agent config";
      });
  },
});

export const { clearCurrentAgentConfig } = agentConfigSlice.actions;
export default agentConfigSlice.reducer;
